import {
	type BalanceSheetId,
	type DataSource,
	Dtos,
	FinancialPeriod,
	type FinancialPeriodId,
	FinancialPeriodPreparationMethod,
	type IncomeStatementId,
	type ISODate,
	PeriodType,
	StatementType,
} from "@inrev/common";

export enum ActiveStatementTypeEnum {
	BalanceSheet = "balance_sheet",
	IncomeStatement = "income_statement",
	Analysis = "analysis",
	DebtSchedule = "debt_schedule",
}
export type ActiveStatementType = `${ActiveStatementTypeEnum}`;

type PeriodSelectorItemBase = {
	date: ISODate;
	typeLabel: string;
	preparationMethod: FinancialPeriodPreparationMethod;
	createdAt: string;
	showRevisionLabel: boolean;
	isActive: boolean;
	/** Whether to badge this row as the active statement — only when its slot has revisions. */
	showActiveIndicator: boolean;
	sourceStatementDate?: ISODate;
	sourceDataSource?: DataSource;
};

/** One selectable balance sheet period row in the FinancialsPeriodSelectorDrawer */
export type BalanceSheetPeriodSelectorItem = PeriodSelectorItemBase & {
	type: "balance_sheet";
	statementType: StatementType;
	id: BalanceSheetId;
};

/** One selectable income statement period row in the FinancialsPeriodSelectorDrawer */
export type IncomeStatementPeriodSelectorItem = PeriodSelectorItemBase & {
	type: "income_statement";
	periodType: PeriodType;
	id: IncomeStatementId;
};

/**
 * One selectable row in the analysis period selector: a single financial period that has an active
 * balance sheet, income statement, or both. Keyed by financial period id.
 */
export type AnalysisPeriodSelectorItem = PeriodSelectorItemBase & {
	type: "analysis";
	id: FinancialPeriodId;
	periodType: PeriodType;
	balanceSheetId?: BalanceSheetId;
	incomeStatementId?: IncomeStatementId;
	hasBalanceSheet: boolean;
	hasIncomeStatement: boolean;
};

export type PeriodSelectorItem =
	| BalanceSheetPeriodSelectorItem
	| IncomeStatementPeriodSelectorItem
	| AnalysisPeriodSelectorItem;

export const isBalanceSheetPeriodSelectorItem = (
	item: PeriodSelectorItem,
): item is BalanceSheetPeriodSelectorItem => item.type === ActiveStatementTypeEnum.BalanceSheet;

export const isIncomeStatementPeriodSelectorItem = (
	item: PeriodSelectorItem,
): item is IncomeStatementPeriodSelectorItem =>
	item.type === ActiveStatementTypeEnum.IncomeStatement;

export const isAnalysisPeriodSelectorItem = (
	item: PeriodSelectorItem,
): item is AnalysisPeriodSelectorItem => item.type === ActiveStatementTypeEnum.Analysis;

export type BalanceSheetWithView = Dtos.Admin.BalanceSheet.Get.Response & {
	view: FinancialPeriod.BalanceSheetView;
};
export type IncomeStatementWithView = Dtos.Admin.IncomeStatement.Get.Response & {
	view: FinancialPeriod.IncomeStatementView;
};
